import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Crown, ShoppingBag, DollarSign, Globe, Gift, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('vendor');
  const [annual, setAnnual] = useState(false);

  const vendorPlans = [
    {
      name: 'Basic', tagline: 'Everyone gets started here', price: '$0', priceDetail: 'Free forever', highlight: false,
      features: ['Full vendor profile & listing', 'Accept bookings with escrow', 'Direct messaging with customers', 'Leave & receive reviews', 'Standard search placement', '10% flat seller fee on paid orders'],
      cta: 'Start free', ctaLink: '/auth?mode=register&role=vendor',
    },
    {
      name: 'Better Visibility', tagline: 'Get found more often', price: '$25', priceDetail: 'per month', highlight: true, badge: 'Popular',
      features: ['Everything in Basic, plus', 'Boosted search ranking', 'Featured in category pages', 'Priority customer support', 'Advanced profile analytics', 'Same 10% flat seller fee (no extra)'],
      cta: 'Upgrade', ctaLink: '/auth?mode=register&role=vendor',
    },
    {
      name: 'Priority Placement', tagline: 'Top-of-page + pro tools', price: '$50', priceDetail: 'per month', highlight: false, badge: 'Most Tools',
      features: ['Everything in Better Visibility, plus', 'Front-page placement rotation', 'Elite badge on your profile', 'Ad placement eligibility', 'Dedicated account manager', 'Early access to new features', 'Same 10% flat seller fee (no extra)'],
      cta: 'Get Priority', ctaLink: '/auth?mode=register&role=vendor',
    },
  ];

  const websiteBuilderPlans = [
    {
      name: 'Free Trial', tagline: '14 days free', price: 'Free', priceDetail: '14-day trial', highlight: false,
      features: ['Full drag-and-drop editor', 'Free subdomain (.entreprenatives.com)', 'All templates included', 'Up to 5 products', 'Basic analytics', 'Email support'],
      cta: 'Start Free Trial', ctaLink: '/create-website',
    },
    {
      name: 'Standard', tagline: 'For small businesses', price: annual ? '$24' : '$30', priceDetail: annual ? 'per month (billed annually)' : 'per month', highlight: true, badge: 'Popular',
      features: ['Everything in Trial, plus', 'Custom domain support', 'Unlimited products', 'Advanced analytics', 'Priority support', 'No extra processing fee — Stripe handles payments directly'],
      cta: 'Get Standard', ctaLink: '/create-website',
    },
    {
      name: 'Elite', tagline: 'For growing brands', price: annual ? '$40' : '$50', priceDetail: annual ? 'per month (billed annually)' : 'per month', highlight: false, badge: '20% Annual Savings',
      features: ['Everything in Standard, plus', 'Custom checkout branding', 'Advanced SEO tools', 'Booking system', 'Collaborator access', 'No extra processing fee — Stripe handles payments directly'],
      cta: 'Go Elite', ctaLink: '/create-website',
    },
  ];

  const PlanCard = ({ plan, index }) => (
    <motion.div key={plan.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
      className={`relative rounded-2xl p-8 ${plan.highlight ? 'bg-white border-2 border-[#8B5CF6] shadow-xl' : 'bg-gray-50 border border-gray-200'}`}>
      {plan.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#8B5CF6] text-white text-sm font-medium rounded-full font-outfit">{plan.badge}</span>}
      <div className="mb-6">
        <h3 className="font-syne text-xl font-bold text-gray-900">{plan.name}</h3>
        <p className="text-gray-500 text-sm font-outfit">{plan.tagline}</p>
      </div>
      <div className="mb-6">
        <span className="font-syne text-4xl font-bold text-gray-900">{plan.price}</span>
        <span className="text-gray-500 text-sm font-outfit ml-2">{plan.priceDetail}</span>
      </div>
      <ul className="space-y-3 mb-8">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-gray-600 font-outfit text-sm">
            <Check className="w-5 h-5 text-[#8B5CF6] flex-shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <Link to={user ? (plan.ctaLink.includes('vendor') ? '/dashboard/vendor/upgrade' : plan.ctaLink) : plan.ctaLink}>
        <Button className={`w-full h-12 font-bold font-outfit ${plan.highlight ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`} data-testid={`plan-cta-${plan.name.toLowerCase()}`}>
          {plan.cta}
        </Button>
      </Link>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="container-main">
          <motion.div className="mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-syne text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="pricing-title">Simple, transparent pricing</h1>
            <p className="text-gray-600 font-outfit text-lg max-w-2xl">
              <strong className="text-gray-900">Zero buyer fees.</strong> What you see is what you pay.
              Vendors pay a single flat <strong className="text-gray-900">10% seller fee</strong> — we only make money when you do.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold font-outfit" data-testid="no-buyer-fees-chip">
                <Check className="w-4 h-4" /> 0% buyer fees
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-sm font-semibold font-outfit" data-testid="ten-pct-seller-chip">
                <Percent className="w-4 h-4" /> 10% flat seller fee
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold font-outfit">
                <Gift className="w-4 h-4" /> Basic plan is free forever
              </span>
            </div>
          </motion.div>

          {/* Founding Vendor public banner */}
          <FoundingVendorBanner />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-12">
            <TabsList className="bg-gray-100 p-1 rounded-full inline-flex">
              <TabsTrigger value="vendor" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm font-outfit">Vendor Plans</TabsTrigger>
              <TabsTrigger value="website" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm font-outfit" data-testid="tab-website-builder">Website Builder</TabsTrigger>
              <TabsTrigger value="consumer" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm font-outfit">Consumer & Referrals</TabsTrigger>
              <TabsTrigger value="fees" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm font-outfit">Fee Structure</TabsTrigger>
            </TabsList>

            {/* Vendor Plans */}
            <TabsContent value="vendor" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {vendorPlans.map((plan, i) => <PlanCard key={plan.name} plan={plan} index={i} />)}
              </div>
            </TabsContent>

            {/* Website Builder Plans */}
            <TabsContent value="website" className="mt-8">
              <div className="flex justify-center mb-6">
                <div className="flex items-center gap-3 bg-gray-100 rounded-full p-1">
                  <button onClick={() => setAnnual(false)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!annual ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Monthly</button>
                  <button onClick={() => setAnnual(true)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${annual ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Annual <span className="text-[#8B5CF6] font-bold">-20%</span></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {websiteBuilderPlans.map((plan, i) => <PlanCard key={plan.name} plan={plan} index={i} />)}
              </div>
            </TabsContent>

            {/* Consumer & Referrals */}
            <TabsContent value="consumer" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Consumer */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-[#8B5CF6]" /></div>
                    <div>
                      <h3 className="font-syne text-xl font-bold text-gray-900">Consumer</h3>
                      <p className="text-gray-500 text-sm font-outfit">Hire Indigenous vendors</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold font-syne text-gray-900 mb-4">Free</p>
                  <ul className="space-y-2.5 mb-6">
                    {['Browse all vendor listings', 'Secure escrow payments', 'Direct messaging with vendors', 'Save favorite vendors', 'Leave reviews', '0% buyer fees — what you see is what you pay'].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600 font-outfit text-sm"><Check className="w-4 h-4 text-[#8B5CF6] flex-shrink-0 mt-0.5" /> {f}</li>
                    ))}
                  </ul>
                  <Link to="/auth?mode=register"><Button className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold">Sign up free</Button></Link>
                </motion.div>

                {/* Referral Program */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-[#8B5CF6]/5 to-[#3B82F6]/5 rounded-2xl p-8 border border-[#8B5CF6]/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center"><Gift className="w-6 h-6 text-[#8B5CF6]" /></div>
                    <div>
                      <h3 className="font-syne text-xl font-bold text-gray-900">Referral Rewards</h3>
                      <p className="text-gray-500 text-sm font-outfit">Earn credits by sharing</p>
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit text-sm mb-4">Every user gets a unique promo code. Share it with friends and earn <strong className="text-[#8B5CF6]">Reward Credits</strong> when they transact on the platform.</p>
                  <div className="bg-white rounded-xl p-4 border border-[#8B5CF6]/10 mb-4">
                    <p className="font-syne font-bold text-gray-900 text-lg mb-2">3% Reward Credits</p>
                    <p className="text-gray-500 text-sm font-outfit">Earn 3% of every successful transaction made by someone you referred, paid as EntrepreNatives Reward Credits.</p>
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {['Unique promo code on signup', 'Share via link, WhatsApp, Twitter', 'Credits for platform use or merchandise', 'Minimum 10 credits to redeem', 'Track referrals in your dashboard', 'No limit on credits earned'].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600 font-outfit text-sm"><Check className="w-4 h-4 text-[#8B5CF6] flex-shrink-0 mt-0.5" /> {f}</li>
                    ))}
                  </ul>
                  <Link to="/invite"><Button className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold">Start Earning Credits</Button></Link>
                </motion.div>
              </div>
            </TabsContent>

            {/* Fee Structure */}
            <TabsContent value="fees" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Vendor Fees */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><DollarSign className="w-6 h-6 text-emerald-600" /></div>
                    <div><h3 className="font-syne text-xl font-bold text-gray-900">Vendor Fee</h3><p className="text-gray-500 font-outfit text-sm">One flat fee. That's it.</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-5 bg-white rounded-xl border border-emerald-200 mb-4">
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0"><Percent className="w-7 h-7 text-emerald-600" /></div>
                    <div>
                      <p className="font-mono text-3xl font-bold text-emerald-600">10%</p>
                      <p className="text-gray-500 text-sm font-outfit">flat, on completed transactions</p>
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit text-sm"><strong>We only make money when you do.</strong> No listing fees, no subscription is required, no hidden charges. A flat 10% is deducted only when an order is paid — it funds payment processing, escrow, dispute protection, and the platform itself.</p>
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-emerald-800 font-outfit text-sm"><strong>Example:</strong> Buyer pays $100 → $10 platform fee → You receive <strong>$90</strong></p>
                  </div>
                </motion.div>

                {/* Buyer Fees */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-purple-600" /></div>
                    <div><h3 className="font-syne text-xl font-bold text-gray-900">Buyer Fee</h3><p className="text-gray-500 font-outfit text-sm">What you see is what you pay</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-5 bg-white rounded-xl border border-purple-200 mb-4">
                    <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0"><Percent className="w-7 h-7 text-purple-600" /></div>
                    <div>
                      <p className="font-mono text-3xl font-bold text-purple-600">0%</p>
                      <p className="text-gray-500 text-sm font-outfit">no buyer fees. ever.</p>
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit text-sm">EntrepreNatives doesn't charge buyers a cent on top of the vendor's price. The number on the vendor's page is the number on your receipt.</p>
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-purple-800 font-outfit text-sm"><strong>Example:</strong> Service costs $100 → You pay <strong>$100</strong></p>
                  </div>
                </motion.div>

                {/* Website Builder Fee */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center"><Globe className="w-6 h-6 text-blue-600" /></div>
                    <div><h3 className="font-syne text-xl font-bold text-gray-900">Website Builder Sales</h3><p className="text-gray-500 font-outfit text-sm">No extra platform cut on your storefront</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-5 bg-white rounded-xl border border-blue-200 mb-4">
                    <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><Percent className="w-7 h-7 text-blue-600" /></div>
                    <div>
                      <p className="font-mono text-3xl font-bold text-blue-600">0%</p>
                      <p className="text-gray-500 text-sm font-outfit">EntrepreNatives platform fee on storefront sales</p>
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit text-sm">You pay the monthly subscription — that's it. Storefront payments go directly to your own Stripe account, and Stripe\u2019s standard processing (2.9% + 30¢) is billed by Stripe straight from that account. We never touch it. Just like an independent business.</p>
                </motion.div>

                {/* Referral Credits */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-gradient-to-br from-[#8B5CF6]/5 to-[#3B82F6]/5 rounded-2xl p-8 border border-[#8B5CF6]/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center"><Gift className="w-6 h-6 text-[#8B5CF6]" /></div>
                    <div><h3 className="font-syne text-xl font-bold text-gray-900">Referral Reward Credits</h3><p className="text-gray-500 font-outfit text-sm">Earn by sharing the platform</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-5 bg-white rounded-xl border border-[#8B5CF6]/20 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center flex-shrink-0"><Percent className="w-7 h-7 text-[#8B5CF6]" /></div>
                    <div>
                      <p className="font-mono text-3xl font-bold text-[#8B5CF6]">3%</p>
                      <p className="text-gray-500 text-sm font-outfit">as Reward Credits per referral transaction</p>
                    </div>
                  </div>
                  <p className="text-gray-600 font-outfit text-sm">Every user gets a unique promo code. When someone you refer makes a transaction, you earn 3% as <strong>EntrepreNatives Reward Credits</strong> (not cash). Redeem credits for platform services or merchandise.</p>
                </motion.div>
              </div>
            </TabsContent>
          </Tabs>

          {/* FAQ CTA */}
          <motion.div className="mt-16 text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="font-syne text-2xl font-bold text-gray-900 mb-4">Have questions?</h2>
            <p className="text-gray-600 font-outfit mb-6">Visit our FAQ or contact support for more information.</p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/faq"><Button variant="outline" className="border-gray-300">View FAQ</Button></Link>
              <Link to="/trust-safety"><Button className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold">Contact Support</Button></Link>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FoundingVendorBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/founding-vendors/status`)
      .then(r => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => {});
  }, []);
  if (!status) return null;
  const pct = Math.min(100, (status.slots_taken / status.slots_total) * 100);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-12 rounded-3xl overflow-hidden relative border border-[#F59E0B]/40"
      style={{ background: 'linear-gradient(135deg, #1a1406 0%, #2a1d06 50%, #0E0E23 100%)' }}
      data-testid="pricing-founding-banner"
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F59E0B]/20 blur-3xl" />
      <div className="relative grid grid-cols-1 md:grid-cols-[1.4fr,1fr] gap-6 p-6 sm:p-10">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#FCD34D] text-xs font-bold mb-4">
            <Crown className="w-3 h-3" /> LIMITED CROWDFUND · 50 SEATS TOTAL
          </div>
          <h2 className="font-syne text-2xl sm:text-3xl font-bold text-white mb-3">
            Become a <span className="text-[#FCD34D]">Founding Vendor</span> — $150 one-time
          </h2>
          <p className="text-[#C4B5FD] font-outfit text-sm sm:text-base mb-5 max-w-xl">
            Help us take EntrepreNatives global. In return: lifetime gold badge, 1 full year of Priority Placement ($600 value), 6 spotlight boosts, and a permanent spot on our public Founding Vendors page.
          </p>
          <div className="max-w-md mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#9CA3AF] font-semibold uppercase">
                <span className="text-[#FCD34D] font-bold">{status.slots_remaining}</span> / {status.slots_total} spots remaining
              </span>
              <span className="text-xs text-[#FCD34D] font-mono">{status.slots_taken}/{status.slots_total} claimed</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #F59E0B, #FCD34D)' }} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/founding-members"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[#0E0E23] text-sm font-bold"
              style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
              data-testid="pricing-founding-cta"
            >
              <Crown className="w-4 h-4" /> See Founding Members
            </Link>
            <Link
              to="/dashboard/vendor/founding"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[#FCD34D] text-sm font-bold border border-[#F59E0B]/40 hover:bg-[#F59E0B]/10 transition"
            >
              Claim my spot
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 content-start">
          {(status.benefits || []).slice(0, 4).map((b, i) => (
            <div key={i} className="rounded-xl p-3 bg-white/[0.04] border border-white/10">
              <p className="text-[11px] font-bold text-[#FCD34D] uppercase tracking-wide mb-1">{b.title}</p>
              <p className="text-[11px] text-[#9CA3AF] leading-snug">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

